<script setup lang="ts">
/**
 * ApprovedResidentRow — renders one row in the Approved / Denied resident table.
 */
import BaseBadge from '@/components/ui/BaseBadge.vue';
import type { ResidentApprovalRequestDto } from '@/api/boardTypes';

defineProps<{
  request: ResidentApprovalRequestDto;
}>();

function initials(name: string): string {
  return name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
</script>

<template>
  <tr class="border-t border-border hover:bg-canvas-soft transition-colors" data-testid="approved-row">
    <!-- Name + email -->
    <td class="px-5 py-3.5">
      <div class="flex items-center gap-2.5">
        <div class="w-8 h-8 rounded-pill bg-navy-100 text-navy-700 grid place-items-center font-semibold text-caption flex-shrink-0">
          {{ initials(request.residentName) }}
        </div>
        <div class="min-w-0">
          <div class="text-body font-semibold text-fg truncate">{{ request.residentName }}</div>
          <div class="text-caption text-muted font-mono tabular-nums truncate">{{ request.residentEmail }}</div>
        </div>
      </div>
    </td>

    <!-- Property -->
    <td class="px-5 py-3.5">
      <span class="text-body text-fg-soft">{{ request.claimedProperty ?? '—' }}</span>
    </td>

    <!-- Verification method -->
    <td class="px-5 py-3.5">
      <span class="text-caption text-muted">
        {{ request.verificationMethod.replace(/([A-Z])/g, ' $1').trim() }}
      </span>
    </td>

    <!-- Status -->
    <td class="px-5 py-3.5">
      <BaseBadge
        :variant="request.status === 'Approved' ? 'approved' : 'denied'"
        :no-dot="false"
      >
        {{ request.status }}
      </BaseBadge>
    </td>

    <!-- Resolved date -->
    <td class="px-5 py-3.5">
      <span class="font-mono tabular-nums text-caption text-muted">
        {{ formatDate(request.resolvedAt) }}
      </span>
    </td>

    <!-- Approved by (first sign-off) -->
    <td class="px-5 py-3.5">
      <span class="text-caption text-muted">
        {{ request.signedOffBy[0]?.approverName ?? '—' }}
      </span>
    </td>
  </tr>
</template>
